import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const SESSION_USER_KEY = 'session_user';

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(SESSION_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Fields that can change server-side without a new login — never persist these to cache
const VOLATILE_FIELDS = ['questionnaire_completed'];

const writeCachedUser = (userData) => {
  try {
    if (userData) {
      const toCache = { ...userData };
      VOLATILE_FIELDS.forEach(f => delete toCache[f]);
      localStorage.setItem(SESSION_USER_KEY, JSON.stringify(toCache));
    } else {
      localStorage.removeItem(SESSION_USER_KEY);
    }
  } catch {
    // Ignore storage failures - auth should still work without local persistence.
  }
};

// Set auth header synchronously at module load — prevents 401 race condition on page refresh
// where child components fire requests before AuthContext's useEffect runs
const _initToken = localStorage.getItem('token');
if (_initToken) axios.defaults.headers.common['Authorization'] = `Bearer ${_initToken}`;

// Token expiry helpers
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // convert to ms
  } catch {
    return null;
  }
};

const msUntilExpiry = (token) => {
  const exp = getTokenExpiry(token);
  return exp ? exp - Date.now() : null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => (localStorage.getItem('token') ? readCachedUser() : null));
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef  = useRef(null);
  const interceptorRef   = useRef(null);
  const sessionToastShown = useRef(false); // prevents duplicate session-expired toasts

  // ── Global 401 / 403 interceptor ────────────────────────────────────────
  useEffect(() => {
    interceptorRef.current = axios.interceptors.response.use(
      res => res,
      err => {
        const url = err?.config?.url || '';
        const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/signup') || url.includes('/auth/refresh');

        if (err?.response?.status === 401 && !isAuthRoute) {
          // Show session-expired toast only once, even if 10 requests fail simultaneously
          if (!sessionToastShown.current) {
            sessionToastShown.current = true;
            toast.error('Session expired. Please log in again.', { duration: 3000 });
            setTimeout(() => { sessionToastShown.current = false; }, 5000);
          }
          _softLogout();
        } else if (err?.response?.status === 403) {
          const detail = err?.response?.data?.detail;
          // Suppress toast for email_not_verified — handled by the calling page
          if (detail?.code === 'email_not_verified') return Promise.reject(err);
          // Only show toast for user-initiated actions (POST/PUT/DELETE/PATCH)
          const method = (err?.config?.method || 'get').toLowerCase();
          if (method !== 'get') {
            const msg = typeof detail === 'string' ? detail : 'Access restricted';
            toast.error(`🔒 ${msg}`, { duration: 4000 });
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptorRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── On mount / token change ─────────────────────────────────────────────
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      scheduleRefresh(token);
    } else {
      writeCachedUser(null);
      setLoading(false);
    }
    return () => clearTimeout(refreshTimerRef.current);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async (attempt = 1) => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      setUser(res.data);
      writeCachedUser(res.data);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        _softLogout();
        return false;
      }

      if (attempt < 2) {
        await new Promise(resolve => setTimeout(resolve, 800));
        return fetchUser(attempt + 1);
      }

      const cachedUser = readCachedUser();
      if (cachedUser) {
        setUser(cachedUser);
        toast.error('Connection issue detected. Using your last known session data.', { duration: 4000 });
      } else {
        toast.error('Unable to verify your session right now. Please retry in a moment.', { duration: 4000 });
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ── Silent refresh — fires when token has <1 day left ──────────────────
  const scheduleRefresh = (tkn) => {
    clearTimeout(refreshTimerRef.current);
    const remaining = msUntilExpiry(tkn);
    if (!remaining || remaining <= 0) return;

    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const fireIn = Math.max(remaining - ONE_DAY_MS, 0);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_URL}/auth/refresh`);
        const { access_token, user: userData } = res.data;
        _storeSession(access_token, userData);
      } catch {
        // refresh failed (token already expired) — interceptor will handle 401
      }
    }, fireIn);
  };

  const _storeSession = (access_token, userData) => {
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('token', access_token);
    writeCachedUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    scheduleRefresh(access_token);
  };

  // Soft logout — clears session silently, shows friendly toast, redirects
  const _softLogout = () => {
    clearTimeout(refreshTimerRef.current);
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    writeCachedUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast('Your session has ended. Please sign in again.', { icon: '🔒' });
    // Small delay so toast is visible before redirect
    setTimeout(() => { window.location.href = '/login'; }, 1200);
  };

  const login = async (identifier, password) => {
    const res = await axios.post(`${API_URL}/auth/login`, { identifier, password });
    const { access_token, user: userData } = res.data;
    // Cache identifier for pre-fill on next login
    localStorage.setItem('saved_identifier', identifier);
    _storeSession(access_token, userData);
    return userData;
  };

  const signup = async (full_name, email, phone, password) => {
    const res = await axios.post(`${API_URL}/auth/signup`, { full_name, email, phone, password });
    // Returns { status: "otp_required", email } — OTP screen handled by SignupPage
    return res.data;
  };

  const verifyOtp = async (email, otp) => {
    const res = await axios.post(`${API_URL}/auth/verify-otp`, { email, otp });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('saved_identifier', email);
    _storeSession(access_token, userData);
    return userData;
  };

  const sendOtp = async (email) => {
    await axios.post(`${API_URL}/auth/send-otp`, { email });
  };

  const logout = async () => {
    clearTimeout(refreshTimerRef.current);
    // Invalidate token server-side (increments token_version, clears Redis cache)
    try { await axios.post(`${API_URL}/auth/logout`); } catch {}
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    writeCachedUser(null);
    delete axios.defaults.headers.common['Authorization'];
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, signup, logout, verifyOtp, sendOtp, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
